/**
 * F-020 — High score LocalStorage adapter.
 *
 * Wraps storage access so the rest of the codebase doesn't have to deal
 * with quota / availability / parse errors. Storage interface (Web
 * Storage API) is injectable so tests can use an in-memory map.
 */

const STORAGE_KEY = "cosmic-suika.highScore.v1";

export interface KVStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface HighScoreStore {
  load(): number;
  save(value: number): void;
  clear(): void;
}

export interface HighScoreStoreOptions {
  storage?: KVStorage;
  storageKey?: string;
}

function defaultStorage(): KVStorage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function createHighScoreStore(
  options: HighScoreStoreOptions = {},
): HighScoreStore {
  const key = options.storageKey ?? STORAGE_KEY;
  const storage = options.storage ?? defaultStorage();

  const load = (): number => {
    if (!storage) return 0;
    const raw = storage.getItem(key);
    if (!raw) return 0;
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0;
  };

  const save = (value: number): void => {
    if (!storage) return;
    if (!Number.isFinite(value) || value < 0) return;
    try {
      storage.setItem(key, String(Math.floor(value)));
    } catch {
      // QuotaExceeded etc — silent fail, score still works in-memory.
    }
  };

  const clear = (): void => {
    storage?.removeItem(key);
  };

  return { load, save, clear };
}

export const __testing = { STORAGE_KEY };

export function createMemoryStorage(): KVStorage {
  const map = new Map<string, string>();
  return {
    getItem: (k) => map.get(k) ?? null,
    setItem: (k, v) => {
      map.set(k, v);
    },
    removeItem: (k) => {
      map.delete(k);
    },
  };
}
